import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money } from '../lib/api';

export default function ReceiveStockModal({ product, onClose, onDone }) {
  const shortage = Math.max(0, -(product.remaining_qty || 0));
  const [qty, setQty] = useState(shortage || '');
  const [cost, setCost] = useState(product.net_cost_per_unit || '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const q = Number(qty) || 0;
  const c = Number(cost) || 0;

  const submit = async () => {
    if (q <= 0) return toast.error('Enter a quantity');
    if (c < 0 || cost === '') return toast.error('Enter the landed cost per unit');
    setSaving(true);
    try {
      await adminApi.post('/admin/purchases', {
        source: 'Manual receipt',
        note,
        items: [{ product_id: product.product_id, qty: q, unit_cost: c }],
      });
      toast.success(`Received ${q} × ${product.name}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="receive-stock-modal">
        <div className="p-6 border-b border-[#e7e7e7] flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Receive stock</h2>
            <p className="text-sm text-[#6b7280]">{product.name} · on hand <span className="mono">{product.remaining_qty}</span></p>
          </div>
          <button onClick={onClose} data-testid="close-receive-modal"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">Quantity received</div>
            <input type="number" min="1" className="rcv-input" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="receive-qty-input" />
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">Landed cost / unit (incl. freight)</div>
            <input type="number" step="any" min="0" className="rcv-input" value={cost} onChange={(e) => setCost(e.target.value)} data-testid="receive-cost-input" />
          </label>
          <label className="block">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">Note</div>
            <input className="rcv-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Supplier, PO #…" data-testid="receive-note-input" />
          </label>
          <div className="bg-[#fafafa] rounded-lg p-3 text-sm space-y-1" data-testid="receive-summary">
            <div className="flex justify-between"><span className="text-[#6b7280]">Total cost</span><span className="mono font-semibold">{money(q * c)}</span></div>
            {shortage > 0 && (
              <div className="flex justify-between text-amber-700"><span>Covers negative stock</span><span className="mono">{Math.min(q, shortage)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[#6b7280]">On hand after</span><span className="mono font-semibold">{(product.remaining_qty || 0) + q}</span></div>
          </div>
        </div>
        <div className="p-6 border-t border-[#e7e7e7] flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-brand" data-testid="receive-submit-btn">{saving ? 'Saving…' : 'Receive'}</button>
        </div>
      </div>
      <style>{`.rcv-input{width:100%;padding:9px 12px;border:1px solid #e7e7e7;border-radius:8px;font-size:14px;} .rcv-input:focus{outline:none;border-color:#d92027;box-shadow:0 0 0 2px #fde8e8;}`}</style>
    </div>
  );
}
